import { type NodoMenu } from '@/shared/components/ui/MenuArbol';
import type { Area, Sesion } from '@/shared/auth/sesion.service';
import { etiquetaDeSeccion } from '@/shared/auth/secciones-de-permiso';

/**
 * Navegacion del sistema en dos niveles, pensada para no perderse:
 *
 * - La sidebar es una lista corta y plana: una entrada por seccion.
 * - Cada seccion agrupa sus pantallas como pestañas arriba del contenido.
 *
 * Cada pestaña declara el area de permisos que la habilita (`sec.has_area`): quien no
 * puede leerla no la ve, y una seccion sin ninguna pestaña visible no aparece. El area de
 * cada pestaña es la de su seccion (ver `secciones-de-permiso.ts`), no la de MAESTROS: asi lo
 * que ve un rol coincide con las columnas de la pantalla de roles. Editar catalogos y
 * parametros sigue exigiendo escritura en Personas (area MAESTROS de la base).
 * Las pantallas con vistas propias las resuelven con `?pestania=` / `?tipo=` dentro de la misma ruta.
 */
export interface PestaniaDeSeccion {
  etiqueta: string;
  ruta: string;
  area: Area;
  /** Pantallas de carga: en celular la app es de solo consulta y no se ofrecen. */
  soloEscritorio?: boolean;
}

export interface Seccion {
  id: string;
  etiqueta: string;
  /** Rutas que pertenecen a la seccion aunque no sean una pestaña (fichas, altas). */
  prefijos: readonly string[];
  pestanias: readonly PestaniaDeSeccion[];
}

const SECCIONES: readonly Seccion[] = [
  {
    id: 'personas',
    etiqueta: etiquetaDeSeccion('personas'),
    prefijos: ['/personas'],
    pestanias: [{ etiqueta: 'Personas y cuentas corrientes', ruta: '/personas', area: 'MAESTROS' }],
  },
  {
    id: 'acopio',
    etiqueta: etiquetaDeSeccion('acopio'),
    prefijos: ['/acopio'],
    pestanias: [
      { etiqueta: 'Romaneos', ruta: '/acopio/romaneos', area: 'ACOPIO' },
      { etiqueta: 'Boletos compraventa', ruta: '/acopio/contratos', area: 'ACOPIO' },
    ],
  },
  {
    id: 'comprobantes',
    etiqueta: etiquetaDeSeccion('comprobantes'),
    prefijos: ['/comprobantes'],
    pestanias: [
      { etiqueta: 'Emitidos', ruta: '/comprobantes', area: 'DOCUMENTOS' },
      {
        etiqueta: 'Emitir',
        ruta: '/comprobantes/emitir',
        area: 'DOCUMENTOS',
        soloEscritorio: true,
      },
    ],
  },
  {
    id: 'cobros-pagos',
    etiqueta: etiquetaDeSeccion('cobros-pagos'),
    prefijos: ['/cobros-y-pagos', '/cuentas', '/administracion'],
    pestanias: [
      { etiqueta: 'Cobros y pagos', ruta: '/cobros-y-pagos', area: 'CUENTAS' },
      { etiqueta: 'Cheques en cartera', ruta: '/cuentas/cheques', area: 'CUENTAS' },
      { etiqueta: 'Cruzar deudas', ruta: '/cuentas/cruzar', area: 'CUENTAS' },
      {
        etiqueta: 'Liquidación a empleados',
        ruta: '/administracion/liquidaciones-empleados',
        area: 'ADMIN',
      },
    ],
  },
  {
    id: 'transporte',
    etiqueta: etiquetaDeSeccion('transporte'),
    prefijos: ['/transporte'],
    pestanias: [
      { etiqueta: 'Viajes y liquidaciones', ruta: '/transporte', area: 'TRANSPORTE' },
      { etiqueta: 'Flota', ruta: '/transporte/flota', area: 'TRANSPORTE' },
    ],
  },
  {
    id: 'stock',
    etiqueta: etiquetaDeSeccion('stock'),
    prefijos: ['/stock'],
    pestanias: [
      { etiqueta: 'Stock', ruta: '/stock', area: 'STOCK' },
      { etiqueta: 'Productos', ruta: '/stock/productos', area: 'STOCK' },
    ],
  },
  {
    id: 'informes',
    etiqueta: etiquetaDeSeccion('informes'),
    prefijos: ['/informes'],
    pestanias: [
      { etiqueta: 'Saldos', ruta: '/informes/saldos', area: 'CUENTAS' },
      { etiqueta: 'Ventas y compras', ruta: '/informes/ventas-compras', area: 'DOCUMENTOS' },
    ],
  },
  {
    id: 'configuracion',
    etiqueta: etiquetaDeSeccion('configuracion'),
    prefijos: ['/sistema'],
    pestanias: [
      { etiqueta: 'Usuarios y roles', ruta: '/sistema/usuarios', area: 'SEGURIDAD' },
      { etiqueta: 'Catálogos', ruta: '/sistema/catalogos', area: 'SEGURIDAD' },
      { etiqueta: 'Tablas de merma', ruta: '/sistema/mermas', area: 'SEGURIDAD' },
      { etiqueta: 'Tarifas y campañas', ruta: '/sistema/tarifas', area: 'SEGURIDAD' },
      { etiqueta: 'Medios de pago y cajas', ruta: '/sistema/medios', area: 'SEGURIDAD' },
      { etiqueta: 'Auditoría', ruta: '/sistema/auditoria', area: 'SEGURIDAD' },
    ],
  },
];

/** Una seccion con solo las pestañas que el usuario puede leer. */
export interface SeccionVisible {
  id: string;
  etiqueta: string;
  pestanias: readonly PestaniaDeSeccion[];
}

export function seccionesVisibles(permisos: Sesion['permisos'], esMovil = false): SeccionVisible[] {
  return SECCIONES.flatMap((seccion) => {
    if (esMovil && !['personas', 'stock'].includes(seccion.id)) return [];
    const pestanias = seccion.pestanias.filter(
      (pestania) =>
        permisos[pestania.area].leer &&
        !(esMovil && pestania.soloEscritorio) &&
        (!esMovil || seccion.id !== 'stock' || pestania.ruta === '/stock'),
    );
    return pestanias.length > 0 ? [{ id: seccion.id, etiqueta: seccion.etiqueta, pestanias }] : [];
  });
}

const sinQuery = (ruta: string) => ruta.split('?')[0] ?? ruta;

/** Área de la pantalla o de una ruta hija, usando la coincidencia más específica. */
export function areaDeRuta(ruta: string): Area | undefined {
  const camino = sinQuery(ruta);
  return SECCIONES.flatMap((seccion) => seccion.pestanias)
    .filter((pestania) => camino === pestania.ruta || camino.startsWith(`${pestania.ruta}/`))
    .sort((a, b) => b.ruta.length - a.ruta.length)[0]?.area;
}

function perteneceA(seccion: Seccion, ruta: string): boolean {
  const camino = sinQuery(ruta);
  return seccion.prefijos.some((prefijo) => camino === prefijo || camino.startsWith(`${prefijo}/`));
}

/** La seccion a la que pertenece la ruta actual, si hay una. */
export function seccionDeRuta(ruta: string): Seccion | undefined {
  return SECCIONES.find((seccion) => perteneceA(seccion, ruta));
}

/** Sidebar: una entrada plana por seccion, que abre su primera pestaña visible. */
export function menuPara(permisos: Sesion['permisos'], esMovil = false): NodoMenu[] {
  return [
    { id: 'inicio', etiqueta: 'Inicio', ruta: '/' },
    ...seccionesVisibles(permisos, esMovil).map((seccion) => ({
      id: seccion.id,
      etiqueta: seccion.etiqueta,
      ruta: seccion.pestanias[0]?.ruta,
    })),
  ];
}

/** Ruta de la entrada de la sidebar que corresponde marcar como activa. */
export function rutaActivaDelMenu(ubicacion: string, permisos: Sesion['permisos']): string {
  if (sinQuery(ubicacion) === '/') return '/';
  const seccion = seccionDeRuta(ubicacion);
  if (!seccion) return ubicacion;
  return (
    seccionesVisibles(permisos).find((visible) => visible.id === seccion.id)?.pestanias[0]?.ruta ??
    ubicacion
  );
}

/** Titulo del encabezado: "Seccion › Pestaña". */
export function tituloPorRuta(ruta: string): string {
  const seccion = seccionDeRuta(ruta);
  if (!seccion) return '';
  const pestania = seccion.pestanias.find((candidata) => candidata.ruta === sinQuery(ruta));
  return pestania && seccion.pestanias.length > 1
    ? `${seccion.etiqueta} › ${pestania.etiqueta}`
    : seccion.etiqueta;
}

/** Destino tras el login. */
export function rutaInicial(): string {
  return '/';
}
